/**
 * Stored on each system as `advanced_options.automation_safety`.
 * The **DeviceController** (local Python process) reads this from Mongo and enforces limits
 * so automation cannot hammer smart plugs or doser pumps after restarts or sensor noise.
 */

export type AutomationSafety = {
    toggle: {
        min_interval_seconds: number
        max_actions_per_hour: number
        max_actions_per_day: number
    }
    dose: {
        min_interval_seconds: number
        max_actions_per_hour: number
        max_actions_per_day: number
    }
    hysteresis: {
        temperature: number
        humidity: number
        default: number
    }
}

export const DEFAULT_AUTOMATION_SAFETY: AutomationSafety = {
    /** Smart plugs (VeSync): lights, fans, heaters, dehumidifiers, etc. */
    toggle: {
        min_interval_seconds: 90,
        max_actions_per_hour: 40,
        max_actions_per_day: 300,
    },
    /** HTTP pump/mixer runs on the nutrient controller (each successful run counts once). */
    dose: {
        min_interval_seconds: 120,
        max_actions_per_hour: 20,
        max_actions_per_day: 120,
    },
    /** Extra band before climate outputs turn off — reduces on/off chatter. */
    hysteresis: {
        temperature: 0.75,
        humidity: 3.0,
        default: 0.5,
    },
}

const LEGACY_LIMIT_KEYS = [
    'min_interval_seconds',
    'max_actions_per_hour',
    'max_actions_per_day',
] as const

/** Merge defaults so older systems without nested `toggle` / `dose` still open in the UI. */
export function mergeAutomationSafetyIntoAdvancedOptions(
    advancedOptions: Record<string, unknown> | undefined
): Record<string, unknown> {
    const base =
        advancedOptions && typeof advancedOptions === 'object'
            ? { ...advancedOptions }
            : {}
    const prev = (
        base.automation_safety && typeof base.automation_safety === 'object'
            ? base.automation_safety
            : {}
    ) as Partial<AutomationSafety> & Record<string, unknown>

    const toggle = { ...DEFAULT_AUTOMATION_SAFETY.toggle }
    const dose = { ...DEFAULT_AUTOMATION_SAFETY.dose }

    // Match DeviceController: flat keys under automation_safety seed both channels before nested overrides.
    for (const lk of LEGACY_LIMIT_KEYS) {
        if (lk in prev && typeof (prev as Record<string, unknown>)[lk] === 'number') {
            const v = (prev as Record<string, number>)[lk]
            ;(toggle as Record<string, number>)[lk] = v
            ;(dose as Record<string, number>)[lk] = v
        }
    }

    base.automation_safety = {
        toggle: {
            ...toggle,
            ...(prev.toggle && typeof prev.toggle === 'object' ? prev.toggle : {}),
        },
        dose: {
            ...dose,
            ...(prev.dose && typeof prev.dose === 'object' ? prev.dose : {}),
        },
        hysteresis: {
            ...DEFAULT_AUTOMATION_SAFETY.hysteresis,
            ...(prev.hysteresis && typeof prev.hysteresis === 'object' ? prev.hysteresis : {}),
        },
    }
    return base
}
